import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {v4 as uuidV4} from 'uuid';
import QuickFilters from 'common/search-filters-controls';
import WithSearchControls from 'common/search-controls/with-search-controls';
import SearchInput from 'common/search-controls/location-input';
import SearchDateTimePickers from 'common/search-controls/date-time-pickers';
import SearchActions from 'common/search-controls/submit-cancel-actions';
import SpotListHeading from '../spot-list-heading';
import SearchControlsInfo from 'common/search-controls/search-controls-info';
import SearchTransientInOutFilter from 'pages/search-transient-event-monthly/components/search-transient-inout-filter';
import optimizely from '@spothero/utils/optimizely';
import {IMMEDIATE_FEATURES} from 'utils/experiment';
import SearchMonthlyPowerBookingToggle from 'common/search-controls/search-monthly-power-booking-toggle/SearchMonthlyPowerBookingToggle';
import {Text, Box} from '@spothero/ui';
import classNames from 'classnames';

const MonthlyFlexStylesOverride = {
    pt: '2',
    background: 'white',
    '.SpotListSearchControls-controls': {
        pb: '4',
        mb: '4',
    },
    lineHeight: '4',
};

const SpotListSearchControls = ({
    isMobile,
    isMonthly,
    isSearching,
    isValidStart,
    numSpots,
    onToggleEventList,
    isPowerBooking,
    searchString,
    searchBounds,
    onInputChange,
    onSearchClick,
    onDateTimeChange,
    onSubmit,
    isAdmin,
    timezone,
    starts,
    ends,
}) => {
    const [inputKey, setInputKey] = useState(uuidV4());
    const [dateTimeKey, setDateTimeKey] = useState(uuidV4());
    const [showActions, setShowActions] = useState(false);

    const {
        search: {
            data: {breadcrumbs, searchType, showNoSpotsFound},
        },
        searchRequest,
    } = useSelector(state => state);

    useEffect(() => {
        // If starts updated elsewhere - like EditTimesModal. Need them to change here as well.

        setDateTimeKey(uuidV4());
    }, [starts]);

    useEffect(() => {
        // If ends updated elsewhere - like EditTimesModal. Need them to change here as well.

        setDateTimeKey(uuidV4());
    }, [ends]);

    const appleMapsPartner =
        searchRequest.partner === 'apple' ||
        searchRequest.partner === 'appleTest';

    const isPowerBookingEnabled = optimizely.isFeatureEnabled(
        IMMEDIATE_FEATURES.ENABLE_POWER_BOOKING
    );

    const showMonthlyFlexToggle =
        isMonthly || isPowerBooking ? isPowerBookingEnabled : false;

    const spotListSearchControlsClasses = classNames('SpotListSearchControls', {
        'SpotListSearchControls-box-shadow': !showMonthlyFlexToggle,
    });

    const onSuggestSelect = currentSearch => {
        // update search input to match with selected suggestion address
        onInputChange(currentSearch);

        setShowActions(true);
    };

    const handleDateTimeChange = data => {
        setShowActions(true);

        onDateTimeChange(data);
    };

    const handleSearchClick = () => {
        setShowActions(false);

        onSearchClick();
    };

    const onCancelClick = () => {
        setInputKey(uuidV4());
        setDateTimeKey(uuidV4());
        setShowActions(false);
    };

    const renderDateTimePicker = () => {
        const monthlyStartDateLabel = (
            <Text
                fontWeight="semibold"
                color="gray.600"
                fontSize="sm"
                textAlign="left"
                mb="2"
            >
                Start Date
            </Text>
        );

        return (
            <SearchDateTimePickers
                key={dateTimeKey}
                isAdmin={isAdmin}
                timezone={timezone}
                isMonthly={isMonthly}
                starts={starts}
                ends={ends}
                onChange={handleDateTimeChange}
                {...(showMonthlyFlexToggle && {monthlyStartDateLabel})}
            />
        );
    };

    const renderSearchInput = () => (
        <SearchInput
            key={inputKey}
            isMonthly={isMonthly}
            isSearching={isSearching}
            searchString={searchString}
            searchBounds={searchBounds}
            onSuggestSelect={onSuggestSelect}
            onChange={onInputChange}
            onSubmit={onSubmit}
        />
    );

    return (
        <div className={spotListSearchControlsClasses}>
            <SearchControlsInfo
                isMobile={isMobile}
                searchType={searchType}
                breadcrumbs={breadcrumbs}
            />
            <Box
                as="div"
                sx={showMonthlyFlexToggle && {...MonthlyFlexStylesOverride}}
            >
                <SpotListHeading
                    showEventsLink={!isMobile}
                    onToggleEventList={onToggleEventList}
                />
                <div
                    className="SpotListSearchControls-controls"
                    data-testid="SpotListSearchControls-controls"
                >
                    {!showMonthlyFlexToggle ? (
                        <>
                            {!appleMapsPartner && renderSearchInput()}
                            {renderDateTimePicker()}
                        </>
                    ) : (
                        <SearchMonthlyPowerBookingToggle
                            renderSearchInput={renderSearchInput}
                            renderDateTimePicker={renderDateTimePicker}
                        />
                    )}
                    {showActions && (
                        <SearchActions
                            addKeyListeners={!isMobile}
                            showCancel
                            searchButtonText="Update Search"
                            isSearching={isSearching}
                            isValidStart={isValidStart}
                            onSearchClick={handleSearchClick}
                            onCancelClick={onCancelClick}
                        />
                    )}
                </div>
            </Box>
            {numSpots > 0 && !showNoSpotsFound && !showActions && (
                <QuickFilters
                    showReservationTypes={isMonthly}
                    visibleSlugs={['self-park']}
                    extraFilterSlot={
                        isMonthly ? null : <SearchTransientInOutFilter />
                    }
                />
            )}
        </div>
    );
};

SpotListSearchControls.propTypes = {
    isMobile: PropTypes.bool,
    isAdmin: PropTypes.bool.isRequired,
    timezone: PropTypes.string.isRequired,
    isMonthly: PropTypes.bool,
    starts: PropTypes.string,
    ends: PropTypes.string,
    searchString: PropTypes.string,
    searchBounds: PropTypes.shape({
        swLat: PropTypes.number,
        swLng: PropTypes.number,
        neLat: PropTypes.number,
        neLng: PropTypes.number,
    }),
    isSearching: PropTypes.bool,
    isValidStart: PropTypes.bool,
    onInputChange: PropTypes.func.isRequired,
    onDateTimeChange: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    onSearchClick: PropTypes.func,
    numSpots: PropTypes.number.isRequired,
    onToggleEventList: PropTypes.func,
    isPowerBooking: PropTypes.bool.isRequired,
};

export default WithSearchControls(SpotListSearchControls);
